/**
 * Converts seconds into a readable duration string (e.g., "1h 50m", "42s").
 */
export function formatDuration(
  seconds: number | string | null | undefined,
): string {
  if (seconds === null || seconds === undefined) return "-";

  const totalSeconds =
    typeof seconds === "string" ? parseInt(seconds, 10) : seconds;

  if (isNaN(totalSeconds) || totalSeconds <= 0) return "-";

  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);

  const parts = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);

  // Show seconds if:
  // 1. It's less than a minute (h is 0, m is 0)
  // 2. We want higher precision (e.g., avgTimePerQ might need seconds)
  // For simplicity, we'll show seconds if h is 0.
  if (h === 0 && s > 0) parts.push(`${s}s`);

  return parts.length > 0 ? parts.join(" ") : "0s";
}

// Backend APIs send timestamps in a few shapes, so every formatter accepts all of them.
export type BackendTimestamp = Date | number | string | null | undefined;

export type StudentLocalTimeOptions = Intl.DateTimeFormatOptions & {
  /** Value to show when the timestamp or timezone cannot be parsed. */
  fallback?: string;
  /** Intl locale used for display formatting, e.g. "en-IN" or "en-US". */
  locale?: string;
};

export type StudentLocalTimeHelperConfig = {
  getTimezoneIdentifier: () => string | null | undefined;
  getLocaleIdentifier?: () => string | null | undefined;
  fallbackTimezoneIdentifier?: string;
  fallbackLocaleIdentifier?: string;
};

// Default output for places that need date and time together.
const DEFAULT_STUDENT_LOCAL_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
};

// Date-only preset, used by cards/lists that render time separately.
const DEFAULT_STUDENT_LOCAL_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "short",
  year: "numeric",
};

// Clock-time-only preset, used for start/end time labels.
const DEFAULT_STUDENT_LOCAL_CLOCK_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
};

// Normalize timezone IDs that are known aliases but not supported consistently everywhere.
const TIMEZONE_IDENTIFIER_ALIASES: Record<string, string> = {
  "America/Honolulu": "Pacific/Honolulu",
};

export const normalizeTimezoneIdentifier = (timeZone: string): string =>
  TIMEZONE_IDENTIFIER_ALIASES[timeZone] ?? timeZone;

export const parseBackendTimestampToDate = (
  timestamp: BackendTimestamp,
): Date | null => {
  if (timestamp === null || timestamp === undefined || timestamp === "") {
    return null;
  }

  if (timestamp instanceof Date) {
    return Number.isNaN(timestamp.getTime()) ? null : timestamp;
  }

  if (typeof timestamp === "number") {
    const millis = timestamp < 1_000_000_000_000 ? timestamp * 1000 : timestamp;
    const date = new Date(millis);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const trimmedTimestamp = timestamp.trim();
  const numericTimestamp = Number(trimmedTimestamp);

  if (Number.isFinite(numericTimestamp) && /^\d+$/.test(trimmedTimestamp)) {
    const millis =
      numericTimestamp < 1_000_000_000_000
        ? numericTimestamp * 1000
        : numericTimestamp;
    const date = new Date(millis);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const backendDateTimeMatch = trimmedTimestamp.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?$/,
  );

  if (backendDateTimeMatch) {
    const [, year, month, day, hour, minute, second] = backendDateTimeMatch;
    return new Date(
      Date.UTC(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second),
      ),
    );
  }

  // Final fallback for ISO strings or other Date-parseable values.
  const date = new Date(trimmedTimestamp);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatWithTimeZone = (
  timestamp: BackendTimestamp,
  timeZone?: string | null,
  options: StudentLocalTimeOptions = {},
  defaultOptions: Intl.DateTimeFormatOptions = DEFAULT_STUDENT_LOCAL_TIME_OPTIONS,
): string => {
  const fallback = options.fallback ?? "-";
  const date = parseBackendTimestampToDate(timestamp);
  const normalizedTimeZone = timeZone
    ? normalizeTimezoneIdentifier(timeZone)
    : undefined;

  if (!date || !normalizedTimeZone) {
    return fallback;
  }

  const { fallback: _fallback, locale = "en-IN", ...formatOptions } = options;

  try {
    return new Intl.DateTimeFormat(locale, {
      ...defaultOptions,
      ...formatOptions,
      timeZone: normalizedTimeZone,
    }).format(date);
  } catch {
    return fallback;
  }
};

// Full local date + time, e.g. "13 Jul 2026, 05:45 PM".
export const formatTimestampInTimeZone = (
  timestamp: BackendTimestamp,
  timeZone?: string | null,
  options: StudentLocalTimeOptions = {},
): string =>
  formatWithTimeZone(
    timestamp,
    timeZone,
    options,
    DEFAULT_STUDENT_LOCAL_TIME_OPTIONS,
  );

// Hermes on iOS can omit or break formatToParts; .format() is stable on both platforms.
const getZonedDateTimeDisplayParts = (
  date: Date,
  timeZone: string,
  locale: string,
): { day: number; month: string; time: string } | null => {
  const normalizedTimeZone = normalizeTimezoneIdentifier(timeZone);

  try {
    const formatOptions: Intl.DateTimeFormatOptions = {
      timeZone: normalizedTimeZone,
    };
    const day = Number(
      new Intl.DateTimeFormat(locale, {
        ...formatOptions,
        day: "numeric",
      }).format(date),
    );
    const month = new Intl.DateTimeFormat(locale, {
      ...formatOptions,
      month: "short",
    }).format(date);
    const time = new Intl.DateTimeFormat(locale, {
      ...formatOptions,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(date);

    if (!Number.isFinite(day) || !month || !time) {
      return null;
    }

    return { day, month, time };
  } catch {
    return null;
  }
};

/** Formats a timestamp as "14 Jul 3:30 PM" in the supplied timezone. */
export const formatCompactTimestampInTimeZone = (
  timestamp: BackendTimestamp,
  timeZone?: string | null,
  options: Pick<StudentLocalTimeOptions, "fallback" | "locale"> = {},
): string => {
  const fallback = options.fallback ?? "-";
  const date = parseBackendTimestampToDate(timestamp);
  if (!date || !timeZone) return fallback;

  const parts = getZonedDateTimeDisplayParts(
    date,
    timeZone,
    options.locale ?? "en-US",
  );
  if (!parts) return fallback;

  const dayPeriod = parts.time.match(/\s(AM|PM)$/i)?.[1]?.toUpperCase();
  const clockTime = parts.time.replace(/\s(AM|PM)$/i, "");
  if (!dayPeriod || !clockTime) return fallback;

  return `${parts.day} ${parts.month} ${clockTime} ${dayPeriod}`;
};
const getOrdinalSuffix = (day: number): string => {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

/** Formats a timestamp as "14th Jul, 3:30 PM" in the supplied timezone. */
export const formatTimestampWithOrdinalInTimeZone = (
  timestamp: BackendTimestamp,
  timeZone?: string | null,
  options: Pick<StudentLocalTimeOptions, "fallback" | "locale"> = {},
): string => {
  const fallback = options.fallback ?? "-";
  const date = parseBackendTimestampToDate(timestamp);
  if (!date || !timeZone) return fallback;

  const parts = getZonedDateTimeDisplayParts(
    date,
    timeZone,
    options.locale ?? "en-US",
  );
  if (!parts) return fallback;

  return `${parts.day}${getOrdinalSuffix(parts.day)} ${parts.month}, ${parts.time}`;
};

// Local date only, e.g. "13 Jul 2026".
export const formatDateInTimeZone = (
  timestamp: BackendTimestamp,
  timeZone?: string | null,
  options: StudentLocalTimeOptions = {},
): string =>
  formatWithTimeZone(
    timestamp,
    timeZone,
    options,
    DEFAULT_STUDENT_LOCAL_DATE_OPTIONS,
  );

// Local clock time only, e.g. "05:45 PM".
export const formatClockTimeInTimeZone = (
  timestamp: BackendTimestamp,
  timeZone?: string | null,
  options: StudentLocalTimeOptions = {},
): string =>
  formatWithTimeZone(
    timestamp,
    timeZone,
    options,
    DEFAULT_STUDENT_LOCAL_CLOCK_TIME_OPTIONS,
  );

const UTC_CLOCK_PATTERN = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/;

const parseUtcClock = (
  time: string,
): { hour: number; minute: number; second: number } | null => {
  const match = String(time).trim().match(UTC_CLOCK_PATTERN);
  if (!match) return null;

  const hour = Number(match[1]);
  const minute = Number(match[2]);
  const second = Number(match[3] ?? 0);
  if (
    !Number.isInteger(hour) ||
    hour < 0 ||
    hour > 23 ||
    !Number.isInteger(minute) ||
    minute < 0 ||
    minute > 59 ||
    !Number.isInteger(second) ||
    second < 0 ||
    second > 59
  ) {
    return null;
  }

  return { hour, minute, second };
};

/** API weekdays: ISO 1–7 (Mon–Sun) and JS 0–6 (Sun–Sat). */
const frequencyDayToJsWeekday = (day: number): number | null => {
  if (!Number.isInteger(day)) return null;
  if (day >= 1 && day <= 7) return day === 7 ? 0 : day;
  if (day >= 0 && day <= 6) return day;
  return null;
};

const utcInstantForClock = (
  utcDate: Date,
  clock: { hour: number; minute: number; second: number },
  dayDelta = 0,
): Date =>
  new Date(
    Date.UTC(
      utcDate.getUTCFullYear(),
      utcDate.getUTCMonth(),
      utcDate.getUTCDate() + dayDelta,
      clock.hour,
      clock.minute,
      clock.second,
    ),
  );

const formatZonedClockLabel = (date: Date, timeZone: string): string | null => {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
      .format(date)
      .replace(/[\u202f\u00a0]/g, " ");
  } catch {
    return null;
  }
};

const formatZonedWeekdayShort = (
  date: Date,
  timeZone: string,
): string | null => {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone,
      weekday: "short",
    }).format(date);
  } catch {
    return null;
  }
};

/**
 * UTC clock ("HH:mm" / "HH:mm:ss") → local 12-hour label in `timeZone`.
 * `anchor` picks the civil date used for DST (defaults to now).
 */
export const formatUtcClockTimeInTimeZone = (
  timingUtc: string,
  timeZone?: string | null,
  anchor?: BackendTimestamp,
): string | null => {
  const clock = parseUtcClock(timingUtc);
  const normalizedTimeZone = timeZone
    ? normalizeTimezoneIdentifier(timeZone)
    : undefined;
  if (!clock || !normalizedTimeZone) return null;

  const anchorDate = parseBackendTimestampToDate(anchor) ?? new Date();
  return formatZonedClockLabel(
    utcInstantForClock(anchorDate, clock),
    normalizedTimeZone,
  );
};

/**
 * UTC weekday + clock → local weekday and clock.
 * The weekday shifts when the offset crosses midnight.
 */
export const formatUtcScheduleSlotInTimeZone = (
  timingUtc: string,
  frequencyDay: number,
  timeZone?: string | null,
  anchor?: BackendTimestamp,
): { day: string; time: string } | null => {
  const clock = parseUtcClock(timingUtc);
  const jsWeekday = frequencyDayToJsWeekday(frequencyDay);
  const normalizedTimeZone = timeZone
    ? normalizeTimezoneIdentifier(timeZone)
    : undefined;
  if (!clock || jsWeekday == null || !normalizedTimeZone) return null;

  const anchorDate = parseBackendTimestampToDate(anchor) ?? new Date();
  const instant = utcInstantForClock(
    anchorDate,
    clock,
    jsWeekday - anchorDate.getUTCDay(),
  );
  const day = formatZonedWeekdayShort(instant, normalizedTimeZone);
  const time = formatZonedClockLabel(instant, normalizedTimeZone);
  if (!day || !time) return null;
  return { day, time };
};
export const createStudentLocalTimeHelpers = ({
  getTimezoneIdentifier,
  getLocaleIdentifier,
  fallbackTimezoneIdentifier = "Asia/Kolkata",
  fallbackLocaleIdentifier = "en-IN",
}: StudentLocalTimeHelperConfig) => {
  const getStudentTimezoneIdentifier = (): string =>
    normalizeTimezoneIdentifier(
      getTimezoneIdentifier() ?? fallbackTimezoneIdentifier,
    );

  const getStudentLocaleIdentifier = (): string =>
    getLocaleIdentifier?.() ?? fallbackLocaleIdentifier;

  const withStudentLocale = (
    options?: StudentLocalTimeOptions,
  ): StudentLocalTimeOptions => ({
    locale: getStudentLocaleIdentifier(),
    ...options,
  });

  return {
    getStudentTimezoneIdentifier,
    getStudentLocaleIdentifier,
    getStudentLocalTime: (
      timestamp: BackendTimestamp,
      options?: StudentLocalTimeOptions,
    ): string =>
      formatTimestampInTimeZone(
        timestamp,
        getStudentTimezoneIdentifier(),
        withStudentLocale(options),
      ),
    getStudentLocalDate: (
      timestamp: BackendTimestamp,
      options?: StudentLocalTimeOptions,
    ): string =>
      formatDateInTimeZone(
        timestamp,
        getStudentTimezoneIdentifier(),
        withStudentLocale(options),
      ),
    getStudentLocalClockTime: (
      timestamp: BackendTimestamp,
      options?: StudentLocalTimeOptions,
    ): string =>
      formatClockTimeInTimeZone(
        timestamp,
        getStudentTimezoneIdentifier(),
        withStudentLocale(options),
      ),
  };
};
